/**
 * workAreaMutations.ts
 *
 * The ONLY place that writes to both `workAreas` and `syncQueue` for work area ops.
 * Every write is atomic — both stores update in one IDB transaction or neither does.
 *
 * IDB RESILIENCE:
 *   - If IDB is unavailable or quota-full, each mutating function calls the API
 *     directly so the operation is never silently lost.
 *   - loadWorkAreasMutation is hydration-only (data came FROM the API); it skips
 *     silently when IDB is unavailable since context already holds the data.
 *   - updateWorkAreaMutation reads existing state from IDB before merging, so it
 *     skips gracefully when IDB is unavailable — the update is not lost because
 *     the component already applied it to context optimistically.
 *
 * Replaces workAreaContext mutation methods:
 *   createWorkspaceWorkArea  → createWorkAreaMutation()
 *   updateWorkspaceWorkArea  → updateWorkAreaMutation()
 *   deleteWorkspaceWorkArea  → deleteWorkAreaMutation()
 *   refreshWorkAreas         → loadWorkAreasMutation()  (hydration, no queue entry)
 */

import { appDB, emitIDBChange } from "../db/client";
import { toWorkAreaRecord } from "../stores/workAreasStore";
import { countPendingOps } from "../stores/syncQueueStore";
import { setPendingCount } from "../stores/syncMetaStore";
import { isIDBAvailable, setIDBStatus, isQuotaError } from "../idbStatus";
import { postWithAuth } from "@/lib/api-helpers";
import type { WorkArea } from "@/types/workarea";
import type { WorkAreaOperationPayload } from "../db/schema";

// ─── Internal helper ──────────────────────────────────────────────

function handleQuotaError(mutationName: string): void {
  setIDBStatus("quota_full");
  console.warn(
    `%c[${mutationName}] IDB quota exceeded mid-session — falling back to direct API`,
    "color: #fb923c; font-weight: bold;"
  );
}

// ─────────────────────────────────────────────────────────────────
// CREATE WORK AREA
// Replaces: workAreaContext.createWorkspaceWorkArea()
// ─────────────────────────────────────────────────────────────────

export async function createWorkAreaMutation(
  workArea: WorkArea,
  workspaceId: string
): Promise<void> {
  const payload: WorkAreaOperationPayload = {
    workspaceId,
    name: workArea.name,
    description: workArea.description,
    icon: workArea.icon,
    accessLevel: workArea.accessLevel,
  };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth("/api/work-area/create", payload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.workAreas, appDB.syncQueue], async () => {
      await appDB.workAreas.put(toWorkAreaRecord(workArea, 0));
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "WORKAREA_CREATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("createWorkAreaMutation");
      await postWithAuth("/api/work-area/create", payload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// UPDATE WORK AREA
// Replaces: workAreaContext.updateWorkspaceWorkArea()
// ─────────────────────────────────────────────────────────────────

export async function updateWorkAreaMutation(
  workAreaId: string,
  updates: Partial<WorkArea>,
  workspaceId: string
): Promise<void> {
  const payload: WorkAreaOperationPayload = {
    workspaceId,
    workAreaId,
    name: updates.name,
    description: updates.description,
    icon: updates.icon,
    accessLevel: updates.accessLevel,
  };

  // ── Direct API path ───────────────────────────────────────────────────────
  // updateWorkAreaMutation reads existing IDB state to merge before writing.
  // If IDB is unavailable we can still call the API with the partial updates
  // since the backend does a $set merge — no need for the full existing record.
  if (!isIDBAvailable()) {
    await postWithAuth(`/api/workArea/${workAreaId}`, payload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    const existing = await appDB.workAreas.get(workAreaId);
    if (!existing) {
      // Record not in IDB yet — call API directly (create path handles this)
      await postWithAuth(`/api/workArea/${workAreaId}`, payload);
      return;
    }

    await appDB.transaction("rw", [appDB.workAreas, appDB.syncQueue], async () => {
      await appDB.workAreas.put({
        ...existing,
        ...updates,
        _id: workAreaId,
        isSync: 0 as 0,
        localUpdatedAt: Date.now(),
      });
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "WORKAREA_UPDATE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("updateWorkAreaMutation");
      await postWithAuth(`/api/workArea/${workAreaId}`, payload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// DELETE WORK AREA
// Replaces: workAreaContext.deleteWorkspaceWorkArea()
// ─────────────────────────────────────────────────────────────────

export async function deleteWorkAreaMutation(
  workAreaId: string,
  workspaceId: string
): Promise<void> {
  const payload: WorkAreaOperationPayload = {
    workspaceId,
    workAreaId,
  };

  // ── Direct API path ───────────────────────────────────────────────────────
  if (!isIDBAvailable()) {
    await postWithAuth(`/api/workArea/${workAreaId}/delete`, payload);
    return;
  }

  // ── IDB path ─────────────────────────────────────────────────────────────
  try {
    await appDB.transaction("rw", [appDB.workAreas, appDB.syncQueue], async () => {
      await appDB.workAreas.delete(workAreaId);
      await appDB.syncQueue.add({
        id: crypto.randomUUID(),
        operationType: "WORKAREA_DELETE",
        payload,
        createdAt: Date.now(),
        isSync: 0 as 0,
        attempts: 0,
        lastAttemptAt: null,
        syncError: null,
        noteId: null,
        workspaceId,
      });
    });

    const count = await countPendingOps();
    await setPendingCount(count);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("deleteWorkAreaMutation");
      await postWithAuth(`/api/workArea/${workAreaId}/delete`, payload);
      return;
    }
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────
// INITIAL HYDRATION (from API — no queue entry)
// Replaces: workAreaContext.refreshWorkAreas()
// ─────────────────────────────────────────────────────────────────

/**
 * Load work areas from the API into IDB.
 * Marks all as isSync: 1 — already exists in MongoDB.
 * Does NOT add to syncQueue.
 *
 * If IDB is unavailable, skip silently — context already holds the data.
 */
export async function loadWorkAreasMutation(workAreas: WorkArea[]): Promise<void> {
  if (!isIDBAvailable()) return;

  try {
    const records = workAreas.map((wa) => toWorkAreaRecord(wa, 1));
    await appDB.workAreas.bulkPut(records);
    emitIDBChange();
  } catch (err: any) {
    if (isQuotaError(err)) {
      handleQuotaError("loadWorkAreasMutation");
      return; // No API fallback — data came FROM the API; context already has it
    }
    throw err;
  }
}
